import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatCurrency, formatDate, getImageUrl } from "@/lib/api";
import {
  Building2, Users, Package, DollarSign, AlertTriangle, TrendingUp, FileText,
  Receipt, Clock, CreditCard, Truck, FileSpreadsheet, Inbox, Settings,
  FileMinus, ClipboardList, Banknote, ScanLine, PackagePlus, BarChart3,
  ClipboardCheck, Briefcase, ShieldCheck, AlertCircle, Circle, Wallet,
} from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line } from "recharts";

const CHART_COLORS = ["#E63946", "#424242", "#10B981", "#F59E0B", "#3B82F6", "#8B5CF6", "#EC4899"];

const ICON_MAP = {
  "file-text": FileText, "receipt": Receipt, "truck": Truck, "file-minus": FileMinus,
  "package": Package, "credit-card": CreditCard, "clipboard-list": ClipboardList,
  "dollar-sign": DollarSign, "banknote": Banknote, "scan-line": ScanLine,
  "package-plus": PackagePlus, "bar-chart-3": BarChart3, "clipboard-check": ClipboardCheck,
  "users": Users, "building-2": Building2, "trending-up": TrendingUp, "clock": Clock,
  "briefcase": Briefcase, "shield-check": ShieldCheck, "inbox": Inbox,
  "alert-triangle": AlertTriangle, "alert-circle": AlertCircle, "circle": Circle,
};

// ==================== WIDGET REGISTRY ====================

export const WIDGET_REGISTRY = {
  key_metrics: { label: "Key Metrics", category: "Overview", description: "Suppliers, customers, products, inventory value, low stock", defaultW: 12, defaultH: 5 },
  quick_actions: { label: "Quick Actions", category: "Overview", description: "Configurable action buttons", defaultW: 12, defaultH: 8 },
  quote_invoice_metrics: { label: "Quote & Invoice Metrics", category: "Sales", description: "Pending quotes, outstanding, overdue, revenue", defaultW: 12, defaultH: 5 },
  pending_email_imports: { label: "Pending Email Imports", category: "Operations", description: "Auto-imported emails awaiting review", defaultW: 12, defaultH: 3 },
  overdue_invoices: { label: "Overdue Invoices", category: "Sales", description: "Invoices past due date", defaultW: 6, defaultH: 8 },
  upcoming_due: { label: "Due This Week", category: "Sales", description: "Invoices due in the next 7 days", defaultW: 6, defaultH: 8 },
  revenue_trend: { label: "Revenue Trend", category: "Charts", description: "Monthly revenue over last 6 months", defaultW: 6, defaultH: 10 },
  invoice_aging: { label: "Invoice Aging", category: "Charts", description: "Aging breakdown of outstanding invoices", defaultW: 6, defaultH: 10 },
  recent_quotes: { label: "Recent Quotes", category: "Sales", description: "Latest 5 quotes", defaultW: 4, defaultH: 9 },
  recent_invoices: { label: "Recent Invoices", category: "Sales", description: "Latest 5 invoices", defaultW: 4, defaultH: 9 },
  recent_payments: { label: "Recent Payments", category: "Payments", description: "Latest 5 payments received", defaultW: 4, defaultH: 9 },
  po_metrics: { label: "Purchase Order Metrics", category: "Purchasing", description: "Active POs, expected deliveries, overdue", defaultW: 12, defaultH: 5 },
  recent_pos: { label: "Recent Purchase Orders", category: "Purchasing", description: "Latest purchase orders", defaultW: 6, defaultH: 9 },
  upcoming_deliveries: { label: "Upcoming Deliveries", category: "Purchasing", description: "Deliveries expected this week", defaultW: 6, defaultH: 9 },
  qr_metrics: { label: "Quote Request Metrics", category: "Purchasing", description: "Pending and received quote requests", defaultW: 12, defaultH: 5 },
  recent_qrs: { label: "Recent Quote Requests", category: "Purchasing", description: "Latest quote requests", defaultW: 6, defaultH: 9 },
  products_by_category: { label: "Products by Category", category: "Charts", description: "Pie chart of products per category", defaultW: 6, defaultH: 10 },
  value_by_category: { label: "Inventory Value by Category", category: "Charts", description: "Bar chart of value per category", defaultW: 6, defaultH: 10 },
  recent_products: { label: "Recently Added Products", category: "Inventory", description: "Latest products added to inventory", defaultW: 6, defaultH: 9 },
  low_stock_items: { label: "Low Stock Items", category: "Inventory", description: "Products below reorder level", defaultW: 6, defaultH: 9 },
  recent_customers: { label: "Recently Added Customers", category: "Operations", description: "Latest customers added", defaultW: 12, defaultH: 8 },
  expense_summary: { label: "Expense Summary", category: "Payments", description: "This month's expenses by category", defaultW: 6, defaultH: 9 },
  top_customers: { label: "Top Customers", category: "Sales", description: "Top 5 customers by revenue", defaultW: 6, defaultH: 9 },
};

export const DEFAULT_ENABLED = [
  "key_metrics", "quick_actions", "quote_invoice_metrics", "pending_email_imports",
  "overdue_invoices", "upcoming_due", "revenue_trend", "invoice_aging",
  "recent_quotes", "recent_invoices", "recent_payments",
  "po_metrics", "recent_pos", "upcoming_deliveries",
  "qr_metrics", "recent_qrs",
  "products_by_category", "value_by_category",
  "recent_products", "low_stock_items", "recent_customers",
];

export function buildDefaultLayout(enabledWidgets) {
  let y = 0;
  const layout = [];
  const queue = [...enabledWidgets];
  while (queue.length > 0) {
    const id = queue.shift();
    const w = WIDGET_REGISTRY[id];
    if (!w) continue;
    if (w.defaultW === 12) {
      layout.push({ i: id, x: 0, y, w: 12, h: w.defaultH });
      y += w.defaultH;
    } else {
      const next = queue[0];
      const nw = next ? WIDGET_REGISTRY[next] : null;
      if (nw && w.defaultW + nw.defaultW <= 12) {
        layout.push({ i: id, x: 0, y, w: w.defaultW, h: w.defaultH });
        layout.push({ i: queue.shift(), x: w.defaultW, y, w: nw.defaultW, h: nw.defaultH });
        y += Math.max(w.defaultH, nw.defaultH);
        if (w.defaultW + nw.defaultW < 12) {
          const third = queue[0];
          const tw = third ? WIDGET_REGISTRY[third] : null;
          if (tw && w.defaultW + nw.defaultW + tw.defaultW <= 12) {
            layout[layout.length - 1] = { ...layout[layout.length - 1] }; // keep
            layout.push({ i: queue.shift(), x: w.defaultW + nw.defaultW, y: y - Math.max(w.defaultH, nw.defaultH), w: tw.defaultW, h: tw.defaultH });
          }
        }
      } else {
        layout.push({ i: id, x: 0, y, w: w.defaultW, h: w.defaultH });
        y += w.defaultH;
      }
    }
  }
  return layout;
}

// ==================== WIDGET COMPONENTS ====================

export function KeyMetricsWidget({ data, navigate }) {
  const metrics = [
    { label: "Active Suppliers", value: data.active_suppliers, icon: Building2, color: "#E63946", bg: "bg-red-50", tid: "metric-suppliers" },
    { label: "Active Customers", value: data.active_customers, icon: Users, color: "#3B82F6", bg: "bg-blue-50", tid: "metric-customers" },
    { label: "Total Products", value: data.total_products, icon: Package, color: "#8B4513", bg: "bg-orange-50", tid: "metric-products" },
    { label: "Inventory Value", value: formatCurrency(data.total_value), icon: DollarSign, color: "#10B981", bg: "bg-green-50", tid: "metric-value", isText: true },
    { label: "Low Stock Alerts", value: data.low_stock_count, icon: AlertTriangle, color: "#E63946", bg: "bg-red-50", tid: "metric-lowstock", alert: data.low_stock_count > 0 },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 h-full">
      {metrics.map((m) => (
        <Card key={m.tid} data-testid={m.tid} className="border border-gray-200 rounded-sm shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">{m.label}</p>
                <p className={`font-heading font-bold ${m.isText ? "text-lg" : "text-2xl"} text-[#1F2937]`}>{m.isText ? m.value : (m.value ?? 0).toLocaleString()}</p>
              </div>
              <div className={`w-8 h-8 rounded-sm ${m.bg} flex items-center justify-center relative`}>
                <m.icon className="w-4 h-4" style={{ color: m.color }} strokeWidth={1.5} />
                {m.alert && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#E63946] rounded-full" />}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function QuickActionsWidget({ data, navigate, quickButtons }) {
  if (!quickButtons?.length) return <p className="text-sm text-gray-400 py-4 text-center">No quick actions configured. <button className="text-[#E63946] underline" onClick={() => navigate("/settings/dashboard")}>Configure</button></p>;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-semibold text-sm text-[#1F2937]">Quick Actions</h3>
        <Button variant="ghost" size="sm" onClick={() => navigate("/settings/dashboard")} className="text-gray-400 hover:text-gray-600 h-7 px-2" data-testid="configure-quick-actions-btn">
          <Settings className="w-3.5 h-3.5 mr-1" /> Configure
        </Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
        {quickButtons.map((btn, i) => {
          const IC = ICON_MAP[btn.icon] || Circle;
          return (
            <button key={i} data-testid={`quick-action-${btn.action_type}`} onClick={() => navigate(btn.url)} className="flex flex-col items-center justify-center p-4 rounded-lg text-white min-h-[90px] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md" style={{ backgroundColor: btn.color }}>
              <IC className="w-6 h-6 mb-1.5" strokeWidth={1.5} />
              <span className="text-[11px] font-semibold text-center leading-tight">{btn.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function QuoteInvoiceMetricsWidget({ data, navigate }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 h-full">
      {(data.pending_approval || 0) > 0 && (
        <Card data-testid="metric-pending-approval" className="border-2 border-amber-300 rounded-sm shadow-sm cursor-pointer" onClick={() => navigate("/quotes?status=Pending+Approval")}>
          <CardContent className="p-4"><div className="flex items-start justify-between"><div><p className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 mb-1">Awaiting Approval</p><p className="font-heading font-bold text-2xl text-amber-700">{data.pending_approval}</p></div><div className="w-8 h-8 rounded-sm bg-amber-50 flex items-center justify-center"><FileText className="w-4 h-4 text-amber-600" /></div></div></CardContent>
        </Card>
      )}
      <Card data-testid="metric-pending-quotes" className="border border-gray-200 rounded-sm shadow-sm cursor-pointer" onClick={() => navigate("/quotes")}><CardContent className="p-4"><div className="flex items-start justify-between"><div><p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Pending Quotes</p><p className="font-heading font-bold text-2xl text-[#1F2937]">{data.pending_quotes}</p></div><div className="w-8 h-8 rounded-sm bg-blue-50 flex items-center justify-center"><FileText className="w-4 h-4 text-blue-600" /></div></div></CardContent></Card>
      <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-4"><div className="flex items-start justify-between"><div><p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Quotes This Month</p><p className="font-heading font-bold text-2xl text-[#1F2937]">{data.quotes_this_month}</p></div><div className="w-8 h-8 rounded-sm bg-purple-50 flex items-center justify-center"><TrendingUp className="w-4 h-4 text-purple-600" /></div></div></CardContent></Card>
      <Card className="border border-gray-200 rounded-sm shadow-sm cursor-pointer" onClick={() => navigate("/invoices")}><CardContent className="p-4"><div className="flex items-start justify-between"><div><p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Outstanding</p><p className="font-heading font-bold text-lg text-[#1F2937]">{formatCurrency(data.total_outstanding)}</p></div><div className="w-8 h-8 rounded-sm bg-orange-50 flex items-center justify-center"><Receipt className="w-4 h-4 text-orange-600" /></div></div></CardContent></Card>
      <Card className="border border-gray-200 rounded-sm shadow-sm cursor-pointer" onClick={() => navigate("/invoices")}><CardContent className="p-4"><div className="flex items-start justify-between"><div><p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Overdue</p><p className={`font-heading font-bold text-lg ${data.overdue_count > 0 ? "text-[#E63946]" : "text-gray-400"}`}>{formatCurrency(data.overdue_amount)}</p><p className="text-[10px] text-gray-400">{data.overdue_count} invoice{data.overdue_count !== 1 ? "s" : ""}</p></div><div className="w-8 h-8 rounded-sm bg-red-50 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-[#E63946]" /></div></div></CardContent></Card>
      <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-4"><div className="flex items-start justify-between"><div><p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Revenue This Month</p><p className="font-heading font-bold text-lg text-green-600">{formatCurrency(data.revenue_this_month)}</p></div><div className="w-8 h-8 rounded-sm bg-green-50 flex items-center justify-center"><DollarSign className="w-4 h-4 text-green-600" /></div></div></CardContent></Card>
    </div>
  );
}

export function PendingEmailImportsWidget({ data, navigate }) {
  if (!data.pending_email_imports || data.pending_email_imports <= 0) return <p className="text-sm text-gray-400 text-center py-2">No pending email imports</p>;
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-sm bg-blue-50 flex items-center justify-center relative">
          <Inbox className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 rounded-full text-[9px] text-white flex items-center justify-center font-bold">{data.pending_email_imports}</span>
        </div>
        <div>
          <p className="font-heading font-bold text-sm text-[#1F2937]">{data.pending_email_imports} Email Import{data.pending_email_imports !== 1 ? "s" : ""} Pending</p>
          <p className="text-xs text-gray-500">
            {data.pending_invoices_import > 0 && <span>{data.pending_invoices_import} invoice{data.pending_invoices_import !== 1 ? "s" : ""}</span>}
            {data.pending_invoices_import > 0 && data.pending_quotes_import > 0 && <span> &middot; </span>}
            {data.pending_quotes_import > 0 && <span>{data.pending_quotes_import} quote{data.pending_quotes_import !== 1 ? "s" : ""}</span>}
          </p>
        </div>
      </div>
      <Button size="sm" onClick={() => navigate("/email-imports")} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm"><Inbox className="w-3.5 h-3.5 mr-1" /> Review</Button>
    </div>
  );
}

export function OverdueInvoicesWidget({ data, navigate }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-[#E63946] flex items-center gap-1.5 mb-3"><AlertTriangle className="w-4 h-4" /> Overdue Invoices</h3>
      <div className="flex-1 overflow-y-auto space-y-1">
        {data.overdue_details?.length > 0 ? data.overdue_details.map((inv) => (
          <div key={inv.invoice_id} className="flex items-center justify-between p-2 rounded-sm hover:bg-red-50 cursor-pointer" onClick={() => navigate(`/invoices/${inv.invoice_id}`)}>
            <div><p className="text-xs font-medium text-[#1F2937]">{inv.invoice_number} - <span className="text-gray-500">{inv.customer_name}</span></p><p className="text-[10px] text-[#E63946]">{inv.days_overdue}d overdue</p></div>
            <span className="text-xs font-bold text-[#E63946]">{formatCurrency(inv.balance)}</span>
          </div>
        )) : <p className="text-xs text-gray-400 py-4 text-center">No overdue invoices</p>}
      </div>
    </div>
  );
}

export function UpcomingDueWidget({ data, navigate }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-orange-600 flex items-center gap-1.5 mb-3"><Clock className="w-4 h-4" /> Due This Week</h3>
      <div className="flex-1 overflow-y-auto space-y-1">
        {data.upcoming_due?.length > 0 ? data.upcoming_due.map((inv) => (
          <div key={inv.invoice_id} className="flex items-center justify-between p-2 rounded-sm hover:bg-orange-50 cursor-pointer" onClick={() => navigate(`/invoices/${inv.invoice_id}`)}>
            <div><p className="text-xs font-medium text-[#1F2937]">{inv.invoice_number} - <span className="text-gray-500">{inv.customer_name}</span></p><p className="text-[10px] text-orange-600">Due: {inv.due_date}</p></div>
            <span className="text-xs font-bold text-orange-600">{formatCurrency(inv.balance)}</span>
          </div>
        )) : <p className="text-xs text-gray-400 py-4 text-center">No invoices due this week</p>}
      </div>
    </div>
  );
}

export function RevenueTrendWidget({ data }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-[#1F2937] mb-3">Monthly Revenue Trend</h3>
      {data.revenue_trend?.length > 0 ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.revenue_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#6B7280" }} />
              <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Line type="monotone" dataKey="revenue" stroke="#E63946" strokeWidth={2} dot={{ fill: "#E63946", r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : <div className="flex-1 flex items-center justify-center text-gray-400 text-xs">No revenue data yet</div>}
    </div>
  );
}

export function InvoiceAgingWidget({ data }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-[#1F2937] mb-3">Invoice Aging</h3>
      {data.aging ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={[
              { name: "Current", value: data.aging.current, fill: "#10B981" },
              { name: "31-60d", value: data.aging.days_31_60, fill: "#F59E0B" },
              { name: "61-90d", value: data.aging.days_61_90, fill: "#F97316" },
              { name: "90+d", value: data.aging.days_90_plus, fill: "#E63946" },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6B7280" }} />
              <YAxis tick={{ fontSize: 10, fill: "#6B7280" }} tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Bar dataKey="value" radius={[2, 2, 0, 0]}>{[{ fill: "#10B981" }, { fill: "#F59E0B" }, { fill: "#F97316" }, { fill: "#E63946" }].map((c, i) => <Cell key={i} fill={c.fill} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : <div className="flex-1 flex items-center justify-center text-gray-400 text-xs">No aging data</div>}
    </div>
  );
}

export function RecentQuotesWidget({ data, navigate }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-[#1F2937] mb-3">Recent Quotes</h3>
      <div className="flex-1 overflow-y-auto">
        {data.recent_quotes?.length > 0 ? data.recent_quotes.map((q) => (
          <div key={q.quote_id} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0 cursor-pointer hover:bg-gray-50 px-1 rounded-sm" onClick={() => navigate(`/quotes/${q.quote_id}`)}>
            <div><p className="text-xs font-mono font-medium text-[#E63946]">{q.quote_number}</p><p className="text-[10px] text-gray-500 truncate max-w-[120px]">{q.customer_name}</p></div>
            <div className="text-right"><p className="text-xs font-medium">{formatCurrency(q.total)}</p><Badge className={`rounded-full text-[9px] px-1.5 ${q.status === "Sent" ? "bg-blue-50 text-blue-700" : q.status === "Accepted" ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}`}>{q.status}</Badge></div>
          </div>
        )) : <p className="text-xs text-gray-400 py-4 text-center">No quotes yet</p>}
      </div>
    </div>
  );
}

export function RecentInvoicesWidget({ data, navigate }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-[#1F2937] mb-3">Recent Invoices</h3>
      <div className="flex-1 overflow-y-auto">
        {data.recent_invoices?.length > 0 ? data.recent_invoices.map((inv) => (
          <div key={inv.invoice_id} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0 cursor-pointer hover:bg-gray-50 px-1 rounded-sm" onClick={() => navigate(`/invoices/${inv.invoice_id}`)}>
            <div><p className="text-xs font-mono font-medium text-[#E63946]">{inv.invoice_number}</p><p className="text-[10px] text-gray-500 truncate max-w-[120px]">{inv.customer_name}</p></div>
            <div className="text-right"><p className="text-xs font-medium">{formatCurrency(inv.total)}</p><Badge className={`rounded-full text-[9px] px-1.5 ${inv.status === "Paid" ? "bg-green-50 text-green-700" : inv.status === "Sent" ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-600"}`}>{inv.status}</Badge></div>
          </div>
        )) : <p className="text-xs text-gray-400 py-4 text-center">No invoices yet</p>}
      </div>
    </div>
  );
}

export function RecentPaymentsWidget({ data, navigate }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-[#1F2937] mb-3">Recent Payments</h3>
      <div className="flex-1 overflow-y-auto">
        {data.recent_payments?.length > 0 ? data.recent_payments.map((p) => (
          <div key={p.payment_id} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0 cursor-pointer hover:bg-gray-50 px-1 rounded-sm" onClick={() => navigate(`/invoices/${p.invoice_id}`)}>
            <div><p className="text-xs font-medium text-green-600">{formatCurrency(p.amount)}</p><p className="text-[10px] text-gray-500">{p.invoice_number} - {p.payment_method}</p></div>
            <span className="text-[10px] text-gray-400">{p.payment_date}</span>
          </div>
        )) : <p className="text-xs text-gray-400 py-4 text-center">No payments yet</p>}
      </div>
    </div>
  );
}

export function POMetricsWidget({ data, navigate }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 h-full">
      <Card className="border border-gray-200 rounded-sm shadow-sm cursor-pointer" onClick={() => navigate("/purchase-orders?status=Sent")}><CardContent className="p-4"><div className="flex items-start justify-between"><div><p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Active POs</p><p className="font-heading font-bold text-2xl text-[#1F2937]">{data.active_pos}</p></div><div className="w-8 h-8 rounded-sm bg-blue-50 flex items-center justify-center"><Truck className="w-4 h-4 text-blue-600" /></div></div></CardContent></Card>
      <Card className="border border-gray-200 rounded-sm shadow-sm cursor-pointer" onClick={() => navigate("/purchase-orders")}><CardContent className="p-4"><div className="flex items-start justify-between"><div><p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Expected This Week</p><p className="font-heading font-bold text-2xl text-[#1F2937]">{data.expected_this_week}</p></div><div className="w-8 h-8 rounded-sm bg-green-50 flex items-center justify-center"><Package className="w-4 h-4 text-green-600" /></div></div></CardContent></Card>
      {data.overdue_pos > 0 && <Card className="border-2 border-red-300 rounded-sm shadow-sm cursor-pointer" onClick={() => navigate("/purchase-orders")}><CardContent className="p-4"><div className="flex items-start justify-between"><div><p className="text-[10px] font-semibold uppercase tracking-wider text-red-600 mb-1">Overdue POs</p><p className="font-heading font-bold text-2xl text-red-600">{data.overdue_pos}</p></div><div className="w-8 h-8 rounded-sm bg-red-50 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-red-600" /></div></div></CardContent></Card>}
    </div>
  );
}

export function RecentPOsWidget({ data, navigate }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-[#1F2937] flex items-center gap-1.5 mb-3"><Truck className="w-4 h-4" /> Recent Purchase Orders</h3>
      <div className="flex-1 overflow-y-auto">
        {data.recent_pos?.length > 0 ? data.recent_pos.map((po) => (
          <div key={po.po_id} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0 cursor-pointer hover:bg-gray-50 px-1 rounded-sm" onClick={() => navigate(`/purchase-orders/${po.po_id}`)}>
            <div><p className="text-xs font-mono font-medium text-[#E63946]">{po.po_number}</p><p className="text-[10px] text-gray-500 truncate max-w-[120px]">{po.supplier_name}</p></div>
            <div className="text-right"><p className="text-xs font-medium">{formatCurrency(po.total)}</p><Badge className={`rounded-full text-[9px] px-1.5 ${po.status === "Fulfilled" ? "bg-green-50 text-green-700" : po.status === "Sent" ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-600"}`}>{po.status}</Badge></div>
          </div>
        )) : <p className="text-xs text-gray-400 py-4 text-center">No purchase orders yet</p>}
      </div>
    </div>
  );
}

export function UpcomingDeliveriesWidget({ data, navigate }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-green-600 flex items-center gap-1.5 mb-3"><Package className="w-4 h-4" /> Upcoming Deliveries</h3>
      <div className="flex-1 overflow-y-auto">
        {data.upcoming_deliveries?.length > 0 ? data.upcoming_deliveries.map((ud) => (
          <div key={ud.po_id} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0 cursor-pointer hover:bg-green-50 px-1 rounded-sm" onClick={() => navigate(`/purchase-orders/${ud.po_id}`)}>
            <div><p className="text-xs font-mono font-medium text-[#E63946]">{ud.po_number}</p><p className="text-[10px] text-gray-500">{ud.supplier_name}</p></div>
            <div className="text-right"><p className="text-[10px] text-green-600 font-medium">In {ud.days_until} day{ud.days_until !== 1 ? "s" : ""}</p><p className="text-[10px] text-gray-400">{formatDate(ud.expected_delivery_date)}</p></div>
          </div>
        )) : <p className="text-xs text-gray-400 py-4 text-center">No upcoming deliveries</p>}
      </div>
    </div>
  );
}

export function QRMetricsWidget({ data, navigate }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 h-full">
      <Card className="border border-gray-200 rounded-sm shadow-sm cursor-pointer" onClick={() => navigate("/quote-requests?status=Sent")}><CardContent className="p-4"><div className="flex items-start justify-between"><div><p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Pending QRs</p><p className="font-heading font-bold text-2xl text-[#1F2937]">{data.pending_qrs}</p></div><div className="w-8 h-8 rounded-sm bg-blue-50 flex items-center justify-center"><FileSpreadsheet className="w-4 h-4 text-blue-600" /></div></div></CardContent></Card>
      <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-4"><div className="flex items-start justify-between"><div><p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Quotes Received (7d)</p><p className="font-heading font-bold text-2xl text-green-600">{data.qr_received_week}</p></div><div className="w-8 h-8 rounded-sm bg-green-50 flex items-center justify-center"><FileSpreadsheet className="w-4 h-4 text-green-600" /></div></div></CardContent></Card>
      {data.overdue_qrs > 0 && <Card className="border-2 border-red-300 rounded-sm shadow-sm cursor-pointer" onClick={() => navigate("/quote-requests?status=Sent")}><CardContent className="p-4"><div className="flex items-start justify-between"><div><p className="text-[10px] font-semibold uppercase tracking-wider text-red-600 mb-1">Overdue QRs</p><p className="font-heading font-bold text-2xl text-red-600">{data.overdue_qrs}</p></div><div className="w-8 h-8 rounded-sm bg-red-50 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-red-600" /></div></div></CardContent></Card>}
    </div>
  );
}

export function RecentQRsWidget({ data, navigate }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-[#1F2937] flex items-center gap-1.5 mb-3"><FileSpreadsheet className="w-4 h-4" /> Recent Quote Requests</h3>
      <div className="flex-1 overflow-y-auto">
        {data.recent_qrs?.length > 0 ? data.recent_qrs.map((qr) => (
          <div key={qr.qr_id} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0 cursor-pointer hover:bg-gray-50 px-1 rounded-sm" onClick={() => navigate(`/quote-requests/${qr.qr_id}`)}>
            <div><p className="text-xs font-mono font-medium text-[#E63946]">{qr.qr_number}</p><p className="text-[10px] text-gray-500 truncate max-w-[120px]">{qr.supplier_name}</p></div>
            <div className="text-right"><p className="text-[10px] text-gray-500">{qr.products_count} products</p><Badge className={`rounded-full text-[9px] px-1.5 ${qr.status === "Quote Received" ? "bg-green-50 text-green-700" : qr.status === "Sent" ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-600"}`}>{qr.status}</Badge></div>
          </div>
        )) : <p className="text-xs text-gray-400 py-4 text-center">No quote requests yet</p>}
      </div>
    </div>
  );
}

export function ProductsByCategoryWidget({ data }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-[#1F2937] mb-3">Products by Category</h3>
      {data.products_by_category?.length > 0 ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart><Pie data={data.products_by_category} dataKey="count" nameKey="category_name" cx="50%" cy="50%" outerRadius={80} label={({ category_name, count }) => `${category_name}: ${count}`} labelLine={false}>{data.products_by_category.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
        </div>
      ) : <div className="flex-1 flex items-center justify-center text-gray-400 text-xs">No product data yet</div>}
    </div>
  );
}

export function ValueByCategoryWidget({ data }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-[#1F2937] mb-3">Inventory Value by Category</h3>
      {data.value_by_category?.length > 0 ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.value_by_category}><CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" /><XAxis dataKey="category_name" tick={{ fontSize: 10, fill: "#6B7280" }} /><YAxis tick={{ fontSize: 10, fill: "#6B7280" }} tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} /><Tooltip formatter={(v) => formatCurrency(v)} /><Bar dataKey="value" fill="#E63946" radius={[2, 2, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
      ) : <div className="flex-1 flex items-center justify-center text-gray-400 text-xs">No inventory data yet</div>}
    </div>
  );
}

export function RecentProductsWidget({ data, navigate }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-[#1F2937] mb-3">Recently Added Products</h3>
      <div className="flex-1 overflow-y-auto space-y-1">
        {data.recent_products?.length > 0 ? data.recent_products.map((p) => (
          <div key={p.product_id} className="flex items-center gap-2 p-1.5 rounded-sm hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/inventory/${p.product_id}`)}>
            <div className="w-8 h-8 rounded-sm bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
              {p.image ? <img src={getImageUrl(p.image)} alt="" className="w-full h-full object-cover" /> : <Package className="w-3.5 h-3.5 text-gray-400" />}
            </div>
            <div className="flex-1 min-w-0"><p className="text-xs font-medium text-[#1F2937] truncate">{p.product_name}</p><p className="text-[10px] text-gray-500 font-mono">{p.sku}</p></div>
            <span className="text-[10px] text-gray-400">{formatDate(p.created_at)}</span>
          </div>
        )) : <p className="text-xs text-gray-400 py-4 text-center">No products added yet</p>}
      </div>
    </div>
  );
}

export function LowStockItemsWidget({ data, navigate }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-[#1F2937] flex items-center gap-1.5 mb-3">Low Stock Items {data.low_stock_count > 0 && <Badge className="bg-[#E63946] text-white text-[9px] rounded-full px-1.5">{data.low_stock_count}</Badge>}</h3>
      <div className="flex-1 overflow-y-auto space-y-1">
        {data.low_stock_items?.length > 0 ? data.low_stock_items.map((p) => (
          <div key={p.product_id} className="flex items-center gap-2 p-1.5 rounded-sm hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/inventory/${p.product_id}`)}>
            <div className="w-8 h-8 rounded-sm bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
              {p.image ? <img src={getImageUrl(p.image)} alt="" className="w-full h-full object-cover" /> : <Package className="w-3.5 h-3.5 text-gray-400" />}
            </div>
            <div className="flex-1 min-w-0"><p className="text-xs font-medium text-[#1F2937] truncate">{p.product_name}</p><div className="flex items-center gap-1.5"><span className="text-[10px] text-[#E63946] font-semibold">Qty: {p.quantity_in_stock}</span><span className="text-[10px] text-gray-400">/ {p.reorder_level}</span></div></div>
            <AlertTriangle className="w-3.5 h-3.5 text-[#F59E0B] shrink-0" />
          </div>
        )) : <p className="text-xs text-gray-400 py-4 text-center">All stock levels healthy</p>}
      </div>
    </div>
  );
}

export function RecentCustomersWidget({ data, navigate }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-[#1F2937] mb-3">Recently Added Customers</h3>
      <div className="flex-1 overflow-y-auto space-y-1">
        {data.recent_customers?.length > 0 ? data.recent_customers.map((c) => (
          <div key={c.customer_id} className="flex items-center gap-2 p-1.5 rounded-sm hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/customers/${c.customer_id}`)}>
            <div className="w-8 h-8 rounded-sm bg-blue-50 flex items-center justify-center shrink-0"><Users className="w-3.5 h-3.5 text-[#3B82F6]" /></div>
            <div className="flex-1 min-w-0"><p className="text-xs font-medium text-[#1F2937] truncate">{c.company_name}</p><p className="text-[10px] text-gray-500">{c.contact_person}</p></div>
            <span className="text-[10px] text-gray-400">{formatDate(c.created_at)}</span>
          </div>
        )) : <p className="text-xs text-gray-400 py-4 text-center">No customers added yet</p>}
      </div>
    </div>
  );
}

export function ExpenseSummaryWidget({ data }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-[#1F2937] mb-3">Expense Summary (This Month)</h3>
      <div className="mb-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-0.5">Total Expenses</p>
        <p className="font-heading font-bold text-xl text-[#E63946]">{formatCurrency(data.total_expenses_month || 0)}</p>
      </div>
      <div className="flex-1 overflow-y-auto space-y-1.5">
        {data.expenses_by_category?.length > 0 ? data.expenses_by_category.map((e, i) => (
          <div key={i} className="flex items-center justify-between p-1.5 rounded-sm bg-gray-50">
            <span className="text-xs text-gray-700">{e.category}</span>
            <span className="text-xs font-semibold text-[#1F2937]">{formatCurrency(e.amount)}</span>
          </div>
        )) : <p className="text-xs text-gray-400 text-center">No expenses this month</p>}
      </div>
    </div>
  );
}

export function TopCustomersWidget({ data, navigate }) {
  return (
    <div className="h-full flex flex-col">
      <h3 className="font-heading font-semibold text-sm text-[#1F2937] mb-3">Top Customers by Revenue</h3>
      <div className="flex-1 overflow-y-auto space-y-1.5">
        {data.top_customers?.length > 0 ? data.top_customers.map((c, i) => (
          <div key={i} className="flex items-center justify-between p-2 rounded-sm hover:bg-gray-50">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-[#E63946] text-white text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
              <span className="text-xs font-medium text-[#1F2937] truncate max-w-[150px]">{c.customer_name}</span>
            </div>
            <div className="text-right">
              <p className="text-xs font-semibold text-green-600">{formatCurrency(c.total_revenue)}</p>
              <p className="text-[10px] text-gray-400">{c.invoice_count} inv.</p>
            </div>
          </div>
        )) : <p className="text-xs text-gray-400 py-4 text-center">No customer revenue data</p>}
      </div>
    </div>
  );
}

// ==================== WIDGET MAP ====================

export const WIDGET_COMPONENTS = {
  key_metrics: KeyMetricsWidget,
  quick_actions: QuickActionsWidget,
  quote_invoice_metrics: QuoteInvoiceMetricsWidget,
  pending_email_imports: PendingEmailImportsWidget,
  overdue_invoices: OverdueInvoicesWidget,
  upcoming_due: UpcomingDueWidget,
  revenue_trend: RevenueTrendWidget,
  invoice_aging: InvoiceAgingWidget,
  recent_quotes: RecentQuotesWidget,
  recent_invoices: RecentInvoicesWidget,
  recent_payments: RecentPaymentsWidget,
  po_metrics: POMetricsWidget,
  recent_pos: RecentPOsWidget,
  upcoming_deliveries: UpcomingDeliveriesWidget,
  qr_metrics: QRMetricsWidget,
  recent_qrs: RecentQRsWidget,
  products_by_category: ProductsByCategoryWidget,
  value_by_category: ValueByCategoryWidget,
  recent_products: RecentProductsWidget,
  low_stock_items: LowStockItemsWidget,
  recent_customers: RecentCustomersWidget,
  expense_summary: ExpenseSummaryWidget,
  top_customers: TopCustomersWidget,
};
